/**
 * Module dependencies.
 */

import { Dispatch, ReactElement, SetStateAction, useMemo } from 'react';
import { ModelRangeSpec } from 'src/types/model-ranges';
import { color, media, units } from '@untile/react-components';
import { ifProp, theme } from 'styled-tools';
import {
  modelRangeSpecs,
  modelRangeSpecsMeasures,
  orderedSpecs
} from 'src/core/content-config/model-ranges';

import filter from 'lodash/filter';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  hasButanePropaneSpecs?: boolean;
  hoveredSpec: ModelRangeSpec | null;
  onHoverSpec: Dispatch<SetStateAction<ModelRangeSpec | null>>;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  align-self: end;
  display: grid;
  grid-template-rows: repeat(6, max-content);
  margin: ${units(24.5)} 0 ${units(7.5)};

  ${media.min('ms')`
    margin-top: ${units(42.5)};
  `}
`;

/**
 * `LabelRow` styled component.
 */

const LabelRow = styled.div<{ isHovered: boolean }>`
  ${theme('typography.styles.small')}

  align-items: center;
  border-bottom: 1px solid ${color('grey50')};
  display: grid;
  grid-column-gap: ${units(1)};
  grid-template-columns: repeat(2, max-content);
  padding: ${units(0.5)} ${units(1.5)};
  text-align: center;
  transition: background-color ${theme('animations.fastTransition')};

  &:last-of-type {
    margin-bottom: ${units(5)};
  }

  ${ifProp(
    'isHovered',
    css`
      background-color: ${color('grey25')};
      z-index: 1;
    `
  )}

  ${media.min('ms')`
    ${theme('typography.styles.p')}
  `}
`;

/**
 * `MeasureLabel` styled component.
 */

const MeasureLabel = styled.span`
  ${theme('typography.styles.small')}

  color: ${color('grey400')};

  ${media.min('ms')`
    ${theme('typography.styles.p')}
  `}
`;

/**
 * `ModelRangeSpecsCard` component.
 */

const ModelRangeSpecsCard = (props: Props): ReactElement => {
  const {
    className,
    hasButanePropaneSpecs = true,
    hoveredSpec,
    onHoverSpec
  } = props;

  const { translate } = useTranslate();
  const normalizedOrderedSpecs = useMemo(() => {
    return hasButanePropaneSpecs
      ? orderedSpecs
      : filter(orderedSpecs, spec => {
          return (
            spec !== modelRangeSpecs.butane && spec !== modelRangeSpecs.propane
          );
        });
  }, [hasButanePropaneSpecs]);

  return (
    <Wrapper className={className}>
      {map(normalizedOrderedSpecs, spec => (
        <LabelRow
          isHovered={hoveredSpec === spec}
          key={spec}
          onMouseEnter={() => onHoverSpec(spec)}
          onMouseLeave={() => onHoverSpec(null)}
        >
          {translate(`common:labels.${spec}`)}

          <MeasureLabel>{modelRangeSpecsMeasures[spec]}</MeasureLabel>
        </LabelRow>
      ))}
    </Wrapper>
  );
};

/**
 * Export `ModelRangeSpecsCard` component.
 */

export default ModelRangeSpecsCard;
